import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useGetBenchmarkedGamesQuery, useLazyGetLatestCompletedAnalysisQuery, useSaveBenchmarkedGamesMutation } from '../../../api/analysis'
import { useMutationFeedback } from '../../../hooks/useMutationFeedback'
import { sameBenchmarks, toBenchmarks } from '../helpers/analysisOverviewHelpers'
import { parseBenchmarks, toAnalysisQueryString } from '../helpers/analysisPaths'
import { Benchmark } from '../types/InternalAnalysis'

/**
 * The analysis' benchmark games, kept in the URL (`comparedGames`, `marketIsos`) and stored in the user setting
 * `analysis_previously_benchmarked_games` on every change, as legacy's overview and feature choices do.
 */
export const useAnalysisBenchmarks = ({ gameId, gameVersion }: { gameId: string; gameVersion?: string | null }) => {
  const { t } = useTranslation()
  const { run, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const benchmarkSettingQuery = useGetBenchmarkedGamesQuery(undefined, { refetchOnMountOrArgChange: true })
  const [saveBenchmarks, saveBenchmarksState] = useSaveBenchmarkedGamesMutation()
  const [getLatestCompletedAnalysis] = useLazyGetLatestCompletedAnalysisQuery()
  const [isChecking, setIsChecking] = useState(false)
  const benchmarks = useMemo(() => parseBenchmarks(searchParams), [searchParams])
  const version = gameVersion || searchParams.get('version')

  const setBenchmarksInUrl = useCallback(
    (next: Benchmark[]) => navigate({ search: toAnalysisQueryString({ benchmarks: next, version }) }, { replace: true }),
    [navigate, version]
  )

  // The URL's benchmarks win, so links to a comparison keep it; without any the setting fills them in, as in legacy.
  const hasReadSetting = useRef(false)
  useEffect(() => {
    if (hasReadSetting.current || !benchmarkSettingQuery.isSuccess) return
    hasReadSetting.current = true
    if (benchmarks.length) return
    const stored = toBenchmarks(benchmarkSettingQuery.data[gameId])
    if (stored.length) setBenchmarksInUrl(stored)
  }, [benchmarkSettingQuery.data, benchmarkSettingQuery.isSuccess, benchmarks.length, gameId, setBenchmarksInUrl])

  const storeBenchmarks = async (next: Benchmark[]) => {
    setBenchmarksInUrl(next)
    const saved = await run(saveBenchmarks({ gameId, benchmarks: next }).unwrap(), { error: t('internal-analysts:analysis_overview_benchmarks_save_failed') })
    if (saved.ok && !sameBenchmarks(toBenchmarks(saved.result[gameId]), next)) notifyError(t('internal-analysts:analysis_overview_benchmarks_not_stored'))
  }

  // Legacy only adds games that have a completed analysis in the picked market.
  const pick = async (picked: Benchmark[]) => {
    setIsChecking(true)
    const added = picked.filter((benchmark) => !benchmarks.some((current) => current.gameId === benchmark.gameId && current.marketIso === benchmark.marketIso))
    const rejected: Benchmark[] = []
    await Promise.all(
      added.map(async (benchmark) => {
        try {
          const latest = await getLatestCompletedAnalysis(benchmark, true).unwrap()
          if (!latest || !Object.keys(latest.answers ?? {}).length) rejected.push(benchmark)
        } catch {
          rejected.push(benchmark)
        }
      })
    )
    setIsChecking(false)
    if (rejected.length) notifyError(t('internal-analysts:analysis_overview_benchmark_rejected', { count: rejected.length }))
    const next = picked.filter((benchmark) => !rejected.includes(benchmark))
    if (!sameBenchmarks(next, benchmarks)) await storeBenchmarks(next)
  }

  const remove = (index: number) => storeBenchmarks(benchmarks.filter((_, position) => position !== index))

  return {
    benchmarks,
    pick,
    remove,
    disabled: isChecking || saveBenchmarksState.isLoading || !benchmarkSettingQuery.isSuccess,
    settingFailed: benchmarkSettingQuery.isError,
  }
}
